import {
  Injectable,
  inject,
  signal,
  computed,
  DestroyRef,
  resource,
} from '@angular/core';
import {
  EventTypes,
  OidcSecurityService,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import { interval, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { sanitizeReturnUrl } from '@core/utils/url-sanitizer';
import { Role, UserData, buildIdentityServerUrls } from 'portal-shared';
import { ApiService, HttpError } from '@services/api.service';
import { CookieService } from './cookie.service';
import { DateTime } from 'luxon';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private oidcSecurityService = inject(OidcSecurityService);
  private eventService = inject(PublicEventsService);
  private router = inject(Router);
  private apiService = inject(ApiService);
  private cookieService = inject(CookieService);
  private destroyRef = inject(DestroyRef);
  private static readonly SESSION_POLL_INTERVAL_MS = 30000;

  private identityServerUrls = buildIdentityServerUrls(
    environment.identityServer.url,
  );

  private listeningForInactivity = false;
  private pollingSession = false;
  private loggingOut = false;

  private readonly _isAuthenticated = signal<boolean>(false);
  readonly isAuthenticated = this._isAuthenticated.asReadonly();

  readonly isAdmin = computed(() => {
    const role = this.userResource.value()?.role;
    return role === Role.ADMIN || role === Role.SYS_ADMIN;
  });

  readonly isAnalyst = computed(() => {
    const role = this.userResource.value()?.role;
    return role === Role.INFLUENCER;
  });

  readonly isParticipant = computed(() => {
    const role = this.userResource.value()?.role;
    return role === Role.PARTICIPANT;
  });

  // User data resource - fetches once on login and keeps the data
  readonly userResource = resource<UserData | null, boolean>({
    request: () => this._isAuthenticated(),
    loader: async ({ request: isAuthenticated, abortSignal }) => {
      if (!isAuthenticated) {
        return null;
      }

      const authResult = await firstValueFrom(
        this.oidcSecurityService.checkAuth(),
      );

      if (!authResult?.isAuthenticated) {
        return null;
      }

      try {
        const userData = await this.apiService.get<UserData>('/user/me', {
          signal: abortSignal,
        });
        return userData ?? null;
      } catch (error) {
        // Re-throw network errors (0) and service unavailable errors (503)
        // so they can be displayed to the user properly
        if (
          error instanceof HttpError &&
          (error.status === 0 || error.status === 503)
        ) {
          throw error;
        }
        console.error('Failed to fetch user data:', error);
        return null;
      }
    },
  });

  constructor() {
    // Only initialize auth if we're not on the login page
    if (!this.isOnLoginPage()) {
      this.initializeAuth();
    }
    this.setupSessionMonitoring();
  }

  private isOnLoginPage(): boolean {
    const pathname = window.location.pathname;
    const hasAuthCallbackParams =
      window.location.search.includes('code=') ||
      window.location.search.includes('state=');

    // Don't initialize if on login page OR if we have OIDC callback parameters anywhere
    return (
      pathname === '/login' ||
      pathname.endsWith('/login') ||
      hasAuthCallbackParams
    );
  }

  private async initializeAuth(): Promise<void> {
    try {
      const authResult = await firstValueFrom(
        this.oidcSecurityService.checkAuth(),
      );
      this._isAuthenticated.set(authResult?.isAuthenticated || false);
    } catch (error) {
      console.error('Auth initialization failed:', error);
      this._isAuthenticated.set(false);
    }
  }

  private setupSessionMonitoring(): void {
    this.eventService
      .registerForEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (notification) => {
        // Handle token renewal events
        if (notification.type === EventTypes.SilentRenewStarted) {
          // Silent renewal started
        }

        if (notification.type === EventTypes.TokenExpired) {
          // Token expired, renewal should happen automatically
        }
      });
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const auth = await firstValueFrom(this.oidcSecurityService.checkAuth());
      if (!auth?.isAuthenticated) return null;
      const token = await firstValueFrom(
        this.oidcSecurityService.getAccessToken(),
      );
      return token || null;
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }

  /**
   * Resolves once userResource has finished loading (settled or errored).
   */
  async waitForUser(): Promise<void> {
    while (this.userResource.isLoading()) {
      await new Promise<void>((resolve) => setTimeout(resolve, 50));
    }
  }

  /**
   * Get the current user data.
   * Returns null if not loaded yet or not authenticated.
   *
   * For reactive use cases, prefer accessing userResource.value() directly
   * or using effect()/computed() to react to changes.
   *
   * Note: This method returns the current cached value and does not trigger loading.
   * The userResource automatically loads when isAuthenticated becomes true.
   */
  async getUserData(): Promise<UserData | null> {
    return this.userResource.value() || null;
  }

  async isUserAuthorized(
    requiredRoles: Role[],
    returnUrl?: string,
  ): Promise<boolean> {
    try {
      // Initialize auth if not already done (for protected routes)
      if (!this._isAuthenticated()) {
        await this.initializeAuth();
      }

      // Always check authentication freshly, don't rely on signal
      const authResult = await firstValueFrom(
        this.oidcSecurityService.checkAuth(),
      );
      const isAuthenticated = authResult?.isAuthenticated || false;

      // Update signal with fresh value
      this._isAuthenticated.set(isAuthenticated);

      if (!isAuthenticated) {
        this.redirectToLogin(returnUrl);
        return false;
      }

      // Wait for userResource to finish loading
      // The resource automatically loads when _isAuthenticated becomes true
      await this.waitForUser();

      // Check if there was an error loading user data
      const error = this.userResource.error();
      if (error) {
        // Re-throw HttpErrors so they can be handled by the guard
        throw error;
      }

      const userData = this.userResource.value();

      if (userData && requiredRoles && requiredRoles.length > 0) {
        if (!requiredRoles.includes(userData.role)) {
          return false;
        }
      }

      return true;
    } catch (error) {
      // Re-throw network errors (0) and service unavailable errors (503)
      // so they can be displayed to the user properly instead of redirecting to login
      if (
        error instanceof HttpError &&
        (error.status === 0 || error.status === 503)
      ) {
        throw error;
      }
      console.error('Error checking user authorization:', error);
      this.redirectToLogin(returnUrl);
      return false;
    }
  }

  login(): void {
    this.oidcSecurityService.authorize('portal-auth-config');
  }

  logout(timeout = false): void {
    // Update auth signal and prevent duplicate redirects
    // This will automatically clear the userResource since it depends on _isAuthenticated
    this._isAuthenticated.set(false);
    this.loggingOut = true;

    // Store logout message to show after redirect
    if (timeout) {
      sessionStorage.setItem('postLogoutMessage', 'timeout');
    } else {
      sessionStorage.setItem('postLogoutMessage', 'logout');
    }

    // Perform full logout to clear identity server session
    // This redirects to identity server logout, then back to postLogoutRedirectUri (/login)
    this.oidcSecurityService.logoff('portal-auth-config').subscribe();

    // Reset logging out flag
    setTimeout(() => {
      this.loggingOut = false;
    }, 100);
  }

  listenForInactivity(): void {
    if (this.listeningForInactivity) return;

    interval(10000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const cookie = this.cookieService.getCookie(
          'PortalAuthExpirationTicket',
        );
        if (cookie) {
          const expiry = Number.parseInt(cookie, 10);
          if (!Number.isFinite(expiry) || expiry < DateTime.now().toMillis()) {
            this.logout(true);
          }
        }
      });

    this.listeningForInactivity = true;

    // Also start session validation polling
    this.startSessionPolling();
  }

  private startSessionPolling(): void {
    if (this.pollingSession) return;

    interval(AuthService.SESSION_POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async () => {
        if (this.loggingOut) return;

        try {
          const token = await this.getAccessToken();
          if (!token) return;

          const response = await fetch(this.identityServerUrls.sessionCheck, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
              this.logout();
            }
            return;
          }

          const result = (await response.json()) as {
            authenticated: boolean;
          };
          if (!result.authenticated) {
            this.logout();
          }
        } catch {
          // Network errors — don't trigger logout
        }
      });

    this.pollingSession = true;
  }

  // Role mapping no longer needed - backend now maps ARConnect roles directly

  private redirectToLogin(returnUrl?: string): void {
    const loginUrl = '/login';
    const sanitizedReturnUrl = sanitizeReturnUrl(returnUrl);
    if (sanitizedReturnUrl) {
      this.router.navigate([loginUrl], {
        queryParams: { returnUrl: sanitizedReturnUrl },
      });
    } else {
      this.router.navigate([loginUrl]);
    }
  }

  // Role redirect logic has been centralized in RoleRedirectService
}
